import React, { useRef, useState } from 'react';
import { defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import menu from '@assets/images/noImage.jpg';
import BaseModal from '@components/common/form/BaseModal';
import ListPage from '@components/common/layout/ListPage';
import PageWrapper from '@components/common/layout/PageWrapper';
import { AppConstants, DEFAULT_TABLE_ITEM_UNLIMIT, GoodsKinds, TIME_FORMAT_DISPLAY, ValueTypes } from '@constants';
import apiConfig from '@constants/apiConfig';
import { FieldTypes } from '@constants/formConfig';
import { foodStatus, goodsKinds } from '@constants/masterData';
import useDragDrop from '@hooks/useDragDrop';
import useListModal from '@hooks/useListModal';
import useQueryParams from '@hooks/useQueryParams';
import useRestaurant from '@hooks/useRestaurant';
import useTranslate from '@hooks/useTranslate';
import { DragDropTableV2 } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import routes from '@routes';
import restaurantSelectors from '@selectors/restaurant';
import {
    calcBeilagePriceCoefficient,
    calcCoefficient,
    calcFoodPriceCoefficient,
    formatMoney,
    mapBeilageTemplateToBeilage,
    parseJson,
} from '@utils';
import { Tag } from 'antd';
import moment from 'moment';

import BeilageModal from './BeilageModal';
import FoodForm from './FoodForm';
import IngredientModal from './IngredientModal';

const message = defineMessages({
    objectName: 'Food',
});
const FoodListPage = () => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const { restaurantId, parentId } = useParams();
    const goodsKindsValues = translate.formatKeys(goodsKinds, ['label']);

    const foodFormRef = useRef();

    const { params: queryParams, serializeParams } = useQueryParams();
    const { currentSettingParse, restaurant } = useRestaurant();
    const [imageUrl, setImageUrl] = useState(null);
    const [activeBeilageIndex, setActiveBeilageIndex] = useState(null);
    const [savedSearchParams, setSavedSearchParams] = useState(null);
    const [isShowModalSelectBeilageFoodData, setIsShowModalSelectBeilageFoodData] = useState(false);
    const [beilageDataSelected, setBeilageDataSelected] = useState([]);
    const [isShowModalSelectBeilageItem, setIsShowModalSelectBeilageItem] = useState(false);

    const queryParameters = new URLSearchParams(window.location.search);
    const groupFoodId = queryParameters.get('groupFoodId');
    const groupName = queryParameters.get('groupName');
    const foodName = queryParameters.get('foodName');
    const currentRestaurant = useSelector(restaurantSelectors.selectDetailRestaurant);

    const isSubFood = !!parentId;

    const {
        data,
        mixinFuncs,
        queryFilter,
        loading,
        pagination,
        detail,
        titleModal,
        executeSave,
        loadingSave,
        form,
        openModal,
        onShowModal,
        onCancelModal,
        setIsChangedFormValues,
        onSave,
        isEditing,
    } = useListModal({
        apiConfig: apiConfig.food,
        options: {
            pageSize: DEFAULT_TABLE_ITEM_UNLIMIT,
            objectName: translate.formatMessage(message.objectName),
        },
        override: (funcs) => {
            funcs.mappingData = (response) => {
                if (response.result === true) {
                    return {
                        data: response.data.content,
                        total: response.data.totalElements,
                    };
                }
            };
            funcs.changeFilter = (filter) => {
                const groupFoodId = queryParams.get('groupFoodId');
                const groupName = queryParams.get('groupName');
                const params = {
                    groupFoodId,
                    groupName,
                    foodName,
                    parentFoodId: parentId,
                    ...filter,
                };
                const filteredParams = Object.fromEntries(Object.entries(params).filter(([_, value]) => value != null));
                mixinFuncs.setQueryParams(serializeParams(filteredParams));
            };


            funcs.getDataDetailMapping = (data) => {
                form.resetFields();
                const settings = parseJson(data.settings);
                setImageUrl(data?.imageUrl);
                return {
                    ...data,
                    beilages: calcBeilagePriceCoefficient(data.beilages || [], 100),
                    happyHoursSetting: data.happyHoursSetting ? mapHappyHoursSetting(data.happyHoursSetting) : [],
                    timeSale: data.timeSale ? data.timeSale.split(';') : [],
                    pluGroup: data.pluGroup ? data.pluGroup.split(';') : [],
                    price: calcFoodPriceCoefficient(parseJson(data.price) || {}, 100),
                    settings,
                    timeFrom: data.timeFrom ? moment(data.timeFrom, TIME_FORMAT_DISPLAY) : '',
                    timeEnd: data.timeEnd ? moment(data.timeEnd, TIME_FORMAT_DISPLAY) : '',
                };
            };
        },
    });

    const onNaviagateToSubFood = (dataRow) => {
        const currentPath = window.location.pathname;
        const currentSearch = window.location.search;
        const newPath = `${currentPath}/${dataRow.id}${currentSearch}&parentFoodId=${dataRow.id}&foodName=${dataRow.name}`;
        navigate(newPath);
    };

    const { sortedData, sortColumn, onDragEnd } = useDragDrop({
        data,
        apiConfig: apiConfig.food.update,
        setTableLoading: () => { },
        indexField: 'rank',
        getList: mixinFuncs.getList,
    });

    const getPriceQrCode = (priceJson) => {
        const qrcodePrice = parseJson(priceJson) || {};
        return qrcodePrice.qr_code || { in_price: 0, out_price: 0 };
    };

    const getPricePickup = (priceJson) => {
        const pickupPrice = parseJson(priceJson) || {};
        return pickupPrice.pickup || { price: 0 };
    };
    const getPriceDeliver = (priceJson) => {
        const deliverPrice = parseJson(priceJson) || {};
        return deliverPrice.deliver || { price: 0 };
    };

    const columns = [
        sortColumn,
        {
            title: translate.formatMessage(commonMessage.image),
            dataIndex: 'imagePath',
            render: (record) => {
                return (
                    <img
                        src={record ? AppConstants.contentRootUrl + record : menu}
                        alt=""
                        width="70px"
                        height="50px"
                        style={{ objectFit: 'cover' }}
                    />
                );
            },
            width: 100,
        },
        { title: 'PLU', dataIndex: 'plu', align: 'center', width: '100px' },
        {
            title: translate.formatMessage(commonMessage.name),
            dataIndex: 'name',
            render: (name, dataRow) =>
                dataRow.kind === GoodsKinds.CHOOSE ? (
                    // eslint-disable-next-line
                    <a onClick={() => onNaviagateToSubFood(dataRow)}>{name}</a>
                ) : (
                    name
                ),
        },
        {
            title: translate.formatMessage(commonMessage.inPrice),
            dataIndex: 'price',
            align: 'center',
            width: '180px',
            render: (value, dataRow) =>
                dataRow.kind === GoodsKinds.COMMON
                    ? formatMoney(getPriceQrCode(value).in_price, currentSettingParse)
                    : '',
        },
        {
            title: translate.formatMessage(commonMessage.outPrice),
            dataIndex: 'price',
            align: 'center',
            width: '180px',
            render: (value, dataRow) =>
                dataRow.kind === GoodsKinds.COMMON
                    ? formatMoney(getPriceQrCode(value).out_price, currentSettingParse)
                    : '',
        },
        {
            title: translate.formatMessage(commonMessage.pickupPrice),
            dataIndex: 'price',
            align: 'center',
            width: '180px',
            render: (value, dataRow) =>
                dataRow.kind === GoodsKinds.COMMON ? formatMoney(getPricePickup(value).price, currentSettingParse) : '',
        },
        {
            title: translate.formatMessage(commonMessage.deliverPrice),
            dataIndex: 'price',
            align: 'center',
            width: '180px',
            render: (value, dataRow) =>
                dataRow.kind === GoodsKinds.COMMON
                    ? formatMoney(getPriceDeliver(value).price, currentSettingParse)
                    : '',
        },
        // mixinFuncs.renderStatusColumn({ width: '120px' }),
        {
            ...mixinFuncs.renderStatusColumn({ width: '120px' }),
            render: (status, record) => {
                const statusConfig = foodStatus.find(opt => opt.value === status);
                if (!statusConfig) return null;

                return (
                    <Tag
                        color={statusConfig.color}
                        style={{ 
                            display: 'inline-block',
                            // width: '100%',
                            textAlign: 'center',
                            fontSize: 14,
                        }}
                    >
                        {translate.formatMessage(statusConfig.label)}
                    </Tag>
                );
            },
        },
        mixinFuncs.renderActionColumn(
            {
                edit: true,
                delete: true,
            },
            { width: '120px' },
        ),
    ];

    const searchFields = [
        {
            key: 'name',
            placeholder: translate.formatMessage(commonMessage.name),
        },
        ...(!parentId
            ? [
                {
                    key: 'kind',
                    placeholder: translate.formatMessage(commonMessage.selectKind),
                    options: goodsKindsValues,
                    type: FieldTypes.SELECT,
                },
            ]
            : []),
    ];

    const mapHappyHoursSetting = (happyHoursSetting) => {
        const happyHoursSettingMapped = {};
        const happyHoursSettingObject = JSON.parse(happyHoursSetting) || {};

        Object.keys(happyHoursSettingObject).forEach((key) => {
            const data = happyHoursSettingObject[key];
            if (data) {
                happyHoursSettingMapped[key] =
                    data?.map((setting) => ({
                        ...setting,
                        price:
                            setting.price_type === ValueTypes.FIXED
                                ? calcCoefficient(setting.price, 100)
                                : setting.price,
                        price_type: setting.price_type || ValueTypes.FIXED,
                    })) || [];
            }
        });

        return happyHoursSettingMapped;
    };

    const getBeilageItemJson = (beilages) => {
        if (beilages && beilages.length > 0) {
            const newBeilageItems = beilages.map((beilageItem) => ({
                ...beilageItem,
                option: beilageItem.option ? 1 : 0,
            }));
            return calcBeilagePriceCoefficient(newBeilageItems, 0.01);
        }
        return [];
    };

    const getPriceJson = (prices) => {
        if (prices) {
            const { qr_code, pickup, deliver } = prices;
            const price = {
                qr_code: {
                    ...qr_code,
                    in_sale_off_percent: qr_code.in_sale_off_percent || 0,
                    out_sale_off_percent: qr_code.out_sale_off_percent || 0,
                },
                pickup: { price: pickup.price || 0, sale_off_percent: pickup.sale_off_percent || 0 },
                deliver: { price: deliver.price || 0, sale_off_percent: deliver.sale_off_percent || 0 },
            };
            return JSON.stringify(calcFoodPriceCoefficient(price, 0.01));
        }
        return '{}';
    };

    const prepareHappyHoursSetting = (happyHoursSetting) => {
        if (!happyHoursSetting) return '';
        const happyHoursSettingMapped = {};
        Object.keys(happyHoursSetting).forEach((key) => {
            const data = happyHoursSetting[key];
            if (data) {
                happyHoursSettingMapped[key] =
                    data?.map((setting) => ({
                        ...setting,
                        price:
                            setting.price_type === ValueTypes.FIXED
                                ? calcCoefficient(setting.price, 0.01)
                                : setting.price,
                    })) || [];
            }
        });
        return JSON.stringify(happyHoursSettingMapped);
    };

    const handleSelectBeilageFoodData = (beilageRaw) => {
        handleIngredientCancel();
        setIsChangedFormValues(true);
        const beilage = {
            id: beilageRaw.id,
            name_food: beilageRaw.foodConditionName,
            price: calcCoefficient(beilageRaw.foodConditionPrice, 100),
            plu: beilageRaw.plu,
            productInfo: beilageRaw.productInfo,
        };

        const beilages = foodFormRef.current.getFieldValue('beilages') || [];

        if (activeBeilageIndex !== null && beilages[activeBeilageIndex]) {
            if (beilages[activeBeilageIndex].food_datas) {
                beilages[activeBeilageIndex].food_datas.push({ ...beilage });
            } else {
                beilages[activeBeilageIndex].food_datas = [{ ...beilage }];
            }
            foodFormRef.current.setFieldsValue({ beilages });
        }

        setBeilageDataSelected((prev) => [...prev, beilage]);
    };

    const handleAddMoreBeilageFoodData = (beilageIndex) => {
        setActiveBeilageIndex(beilageIndex);
        const beilages = foodFormRef.current.getFieldValue('beilages') || [];
        setBeilageDataSelected(beilages?.[beilageIndex]?.food_datas || []);
        setSavedSearchParams(location.search);
        setIsShowModalSelectBeilageFoodData(true);
    };

    const handleSelectBeilageItem = (beilageRaw) => {
        const beilages = foodFormRef.current.getFieldValue('beilages') || [];
        foodFormRef.current.setFieldsValue({
            beilages: [...beilages, ...mapBeilageTemplateToBeilage(beilageRaw)],
        });
        handleBeilageCancel();
    };

    const openModalSelectBeilageTemplate = () => {
        setIsShowModalSelectBeilageItem(true);
        setSavedSearchParams(location.search);
    };

    const handleIngredientCancel = () => {
        setIsShowModalSelectBeilageFoodData(false);
    };

    const handleBeilageCancel = () => {
        setIsShowModalSelectBeilageItem(false);
    };

    const handleSubmit = (values) => {
        if (!detail) {
            const preparedData = {
                ...values,
                kind: isSubFood ? GoodsKinds.COMMON : values.kind,
                beilages: getBeilageItemJson(values.beilages),
                price: getPriceJson(values.price),
                timeFrom: values.timeFrom ? values.timeFrom.format(TIME_FORMAT_DISPLAY) : '',
                timeEnd: values.timeEnd ? values.timeEnd.format(TIME_FORMAT_DISPLAY) : '',
                settings: JSON.stringify({ ...values.settings }),
                groupFoodId: groupFoodId,
                imagePath: imageUrl || values.imagePath || null,
                parentId: parentId || null,
                restaurantId: currentRestaurant.id,
                ...((values.kind === GoodsKinds.COMMON || isSubFood) && {
                    happyHoursSetting: prepareHappyHoursSetting(values.happyHoursSetting),
                }),
            };
            onSave(preparedData);
        } else {
            const settings = detail.settings || {};
            const preparedData = {
                ...values,
                id: detail.id,
                parentId: parentId || null,
                beilages: getBeilageItemJson(values.beilages),
                price: getPriceJson(values.price),
                pluGroup: values.pluGroup ? values.pluGroup.join(';') : '',
                timeSale: values.timeSale ? values.timeSale.join(';') : '',
                outPrice: values.outPrice || 0,
                outSaleOff: values.outSaleOff || 0,
                timeFrom: values.timeFrom ? values.timeFrom.format(TIME_FORMAT_DISPLAY) : '',
                timeEnd: values.timeEnd ? values.timeEnd.format(TIME_FORMAT_DISPLAY) : '',
                settings: JSON.stringify({ ...settings, ...values.settings }),
                groupFoodId: groupFoodId,
                imagePath: imageUrl || values.imagePath || null,
                restaurantId: currentRestaurant.id,
                happyHoursSetting: prepareHappyHoursSetting(values.happyHoursSetting),
            };
            onSave(preparedData);
        }
        setImageUrl(null);
    };

    return (
        <PageWrapper
            routes={[
                {
                    breadcrumbName: translate.formatMessage(message.objectName),
                    path: generatePath(routes.menuListPage.path, { restaurantId }),
                },
                {
                    breadcrumbName: groupName,
                    path: parentId
                        ? `${generatePath(routes.menuFoodListPage.path, { restaurantId })}?groupFoodId=${groupFoodId}&groupName=${groupName}`
                        : undefined,
                },
                ...(parentId ? [{ breadcrumbName: foodName || 'Sub Foods' }] : []),
            ]}
        >
            <ListPage
                searchForm={mixinFuncs.renderSearchForm({ fields: searchFields, initialValues: queryFilter })}
                actionBar={mixinFuncs.renderActionBar({
                    objectName: translate.formatMessage(commonMessage.food),
                })}
                baseTable={
                    <DragDropTableV2
                        onChange={mixinFuncs.changePagination}
                        columns={columns}
                        dataSource={sortedData || []}
                        onDragEnd={onDragEnd}
                        pagination={pagination}
                        style={{ cursor: 'pointer' }}
                        loading={loading || loadingSave}
                    />
                }
            />

            {openModal && (
                <BaseModal
                    isModalVisible={openModal}
                    isEditing={isEditing}
                    handleCancel={() => {
                        onCancelModal();
                        setImageUrl(null);
                    }}
                    form={form}
                    title={!isEditing ? translate.formatMessage(commonMessage.addFood) : translate.formatMessage(commonMessage.updateFood)}
                    customStyle={{ height: '950px', padding: '0' }}
                    noBodyPadding={true}
                >
                    <div style={{ padding: '12px 16px' }}>
                        <FoodForm
                            formRef={foodFormRef}
                            form={form}
                            isEditing={isEditing}
                            dataDetail={isEditing ? detail : { kind: GoodsKinds.COMMON }}
                            restaurantDetail={currentRestaurant?.settings && JSON?.parse(currentRestaurant?.settings)}
                            imageUrl={imageUrl}
                            setImageUrl={setImageUrl}
                            onSubmit={handleSubmit}
                            handleAddMoreBeilage={handleAddMoreBeilageFoodData}
                            openModalSelectBeilageTemplate={openModalSelectBeilageTemplate}
                            setIsChangedFormValues={setIsChangedFormValues}
                            isSubFood={isSubFood}
                        />
                    </div>
                </BaseModal>
            )}
            <BaseModal
                isModalVisible={isShowModalSelectBeilageFoodData}
                handleCancel={handleIngredientCancel}
                title={translate.formatMessage(commonMessage.selectBeilage)}
                centered
                hideSave={true}
                bodyStyle={{ maxHeight: '90vh', overflowY: 'auto' }}
            >
                <IngredientModal
                    currentRestaurant={currentRestaurant}
                    handleSelectBeilage={handleSelectBeilageFoodData}
                    beilageDataSelected={beilageDataSelected}
                />
            </BaseModal>
            <BaseModal
                isModalVisible={isShowModalSelectBeilageItem}
                handleCancel={handleBeilageCancel}
                title={translate.formatMessage(commonMessage.selectBeilage)}
                centered
                hideSave={true}
            >
                <BeilageModal currentRestaurant={currentRestaurant} handleSelectBeilageItem={handleSelectBeilageItem} />
            </BaseModal>
        </PageWrapper>
    );
};

export default FoodListPage;
